import { trackEvent, trackPageView } from '@finn-no/pulse-sdk';
import { addDays, parseISO, safeParseISO } from 'shared/util/datetime-fns';
import type { Filters, HotelOffer, HotelResponse, Offer, OffersResponse, TrackingPageType } from '@/interfaces';
import type { LmsCheckboxTree, LmsFilters, LmsOffer } from '@/interfaces/lms';

// This is used for tracking both real and virtual page views, i.e. when changing filters or switching pages
// Pulse tracker id: TRP_pv01 (see tracking section in README.md)
export const trackResultPage = (results: OffersResponse, filters: Filters, pageType: TrackingPageType): void => {
    const dateFilters = () => {
        if (filters.ignoredFilters.includes('departureDate')) {
            return [
                {
                    name: 'travel_months',
                    value: filters.travelMonths.map((month) => `${month.year}-${month.month}`).join(','),
                },
                {
                    name: 'duration',
                    value: filters.duration?.join('-'),
                },
            ];
        }
        return [
            {
                name: 'departure_date',
                value: filters.departureDate?.date,
            },
            {
                name: 'departure_date_flexibility',
                value: filters.departureDate?.flexibility?.toString(),
            },
            {
                name: 'return_date',
                value: filters.returnDate?.date,
            },
            {
                name: 'return_date_flexibility',
                value: filters.returnDate?.flexibility?.toString(),
            },
        ];
    };

    const objectKeysToFilters = (name: string, object: Record<string, unknown>): { name: string; value: string }[] =>
        Object.keys(object).map((key) => ({ name, value: key }));

    trackPageView({
        object: {
            type: 'Listing',
            category: 'travel > prepackage',
            layout: pageType,
            filters: {
                // If nothing is filled in, set it to `undefined` --> the Pulse client won't send `query`
                query: filters.keywords || undefined,
            },
            sortingType: filters.sort ?? 'best',
            numItems: results.numberOfHotels,
            pageNumber: results.currentPage,
            selectionFilters: [
                {
                    name: 'origin',
                    value: filters.origin,
                },
                {
                    name: 'destination',
                    value: filters.destinationLabel,
                },
                {
                    name: 'price_range',
                    value: filters.priceRange?.join('-'),
                },
                {
                    name: 'number_of_adults',
                    value: filters.numberOfAdults ? filters.numberOfAdults.toString() : '2',
                },
                {
                    name: 'age_of_child_1',
                    value: filters.agesOfChildren ? filters.agesOfChildren[0]?.toString() : undefined,
                },
                {
                    name: 'age_of_child_2',
                    value: filters.agesOfChildren ? filters.agesOfChildren[1]?.toString() : undefined,
                },
                {
                    name: 'rating',
                    value: Object.keys(filters.rating).join(','),
                },
                {
                    name: 'distance_to_beach',
                    value: filters.distanceToBeach?.join('-'),
                },
                {
                    name: 'distance_to_city',
                    value: filters.distanceToCity?.join('-'),
                },
            ]
                .concat(dateFilters())
                .concat(objectKeysToFilters('meal', filters.meals)) // TODO should fields with multiple values be joined in 1 string?
                .concat(objectKeysToFilters('facility', filters.facilities))
                .concat(objectKeysToFilters('activity', filters.activities))
                .concat(objectKeysToFilters('suppliers', filters.suppliers))
                .filter((filter): filter is { name: string; value: string } => !!(filter.name && filter.value)),
        },
        vertical: {
            name: 'travel',
            subVertical: 'prepackage',
        },
    });
};

// Pulse tracker id: TRP_pv02 (see tracking section in README.md)
export const trackHotelPage = (hotel: HotelResponse) =>
    trackPageView({
        object: {
            id: `prepackage:${hotel.id}`,
            url: 'https://www.finn.no/reise/pakkereiser/hotell/',
            type: 'ClassifiedAd',
            contentId: `prepackage:${hotel.id}`,
            name: hotel.name,
            category: 'travel > prepackage',
        },
        vertical: {
            name: 'travel',
            subVertical: 'prepackage',
        },
    });

// Pulse tracker id: TRP_ev03 (see tracking section in README.md)
export const trackClickOfferHotel = (offer: HotelOffer, hotel: HotelResponse) =>
    trackClickOffer(
        offer.offerId,
        hotel.id,
        hotel.name,
        offer.originAirport,
        hotel.country,
        hotel.region,
        offer.destinationAirport,
        offer.departureDate,
        offer.returnDate,
        offer.pricePerPerson,
        offer.supplier,
        offer.deepLink,
        offer.numberOfAdults ?? 2,
        offer.numberOfChildren ?? 0,
    );

// Pulse tracker id: TRP_ev01 (see tracking section in README.md)
export const trackClickOfferResult = ({
    offerId,
    hotelId,
    hotelName,
    originAirportCode,
    country,
    region,
    destinationAirportCode,
    departureDate,
    returnDate,
    pricePerPerson,
    supplier,
    deepLink,
    numberOfAdults,
    numberOfChildren,
}: Offer) =>
    trackClickOffer(
        offerId,
        hotelId,
        hotelName,
        originAirportCode,
        country,
        region,
        destinationAirportCode,
        departureDate,
        returnDate,
        pricePerPerson,
        supplier,
        deepLink,
        numberOfAdults ?? 2,
        numberOfChildren ?? 0,
    );

// Pulse tracker id: TRP_ev03 (see tracking section in README.md)
const trackClickOffer = (
    offerId: string,
    hotelId: string,
    hotelName: string,
    originAirportCode: string,
    country: string,
    region: string,
    destinationAirportCode: string,
    departureDate: string,
    returnDate: string,
    pricePerPerson: number,
    supplier: string,
    deepLink: string,
    numberOfAdults: number,
    numberOfChildren: number,
) =>
    trackEvent({
        type: 'Click',
        intent: 'Purchase',
        name: 'Purchase prepackage',

        object: {
            id: offerId,
            type: 'UIElement',
            elementType: 'Button',
            items: [
                {
                    id: 'finntravel',
                    type: 'Travel',
                    departureCountry: 'Norge',
                    departureCity: originAirportCode, // Only have the IATA code, not the city name (TODO have a lookup table?)
                    departureAirport: originAirportCode,
                    // Due to legacy definitions in Pulse, `destination` is used instead of `arrivalCountry`
                    destination: country,
                    arrivalCity: region,
                    arrivalAirport: destinationAirportCode,
                    travelType: 'prepackage',
                    departureDate: safeParseISO(departureDate)?.toISOString(),
                    returnDate: safeParseISO(returnDate)?.toISOString(),
                    totalPrice: pricePerPerson * 2, // TODO is `* 2` good enough?
                    provider: supplier,
                    numberOfAdults: numberOfAdults,
                    numberOfChildren: numberOfChildren ?? 0,
                },
                {
                    id: `prepackage:${hotelId}`,
                    type: 'ClassifiedAd',
                    contentId: `prepackage:${hotelId}`,
                    name: hotelName,
                    category: 'travel > prepackage',
                },
            ],
        },
        target: {
            id: deepLink,
            type: 'ExternalContent',
            name: 'Go to webpage',
            referralCommission: true,
            provider: supplier,
        },
        vertical: {
            name: 'travel',
            subVertical: 'prepackage',
        },
    });

// Pulse tracker id: TRP_ev02 (see tracking section in README.md)
export const trackSeeMoreOffers = (offerId: string, hotelName: string) =>
    trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'View more travel options',
        object: {
            id: `view_more_travel_options_${offerId}`,
            type: 'UIElement',
            elementType: 'Link',
        },
        target: {
            id: offerId,
            type: 'ClassifiedAd',
            contentId: offerId,
            name: hotelName,
            category: 'travel > prepackage',
        },
        vertical: {
            name: 'travel',
            subVertical: 'prepackage',
        },
    });

export const trackTicketOfferClick = (offerId: string, hotelName: string, intent: 'Open' | 'Purchase') =>
    trackEvent({
        type: 'Click',
        intent: intent,
        name: 'Click on Ticket result card',
        object: {
            id: `click_on_offer_${offerId}`,
            type: 'UIElement',
            elementType: 'Link',
        },
        target: {
            id: offerId,
            type: 'ClassifiedAd',
            contentId: offerId,
            name: hotelName,
            category: 'travel > prepackage',
        },
        vertical: {
            name: 'travel',
            subVertical: 'prepackage',
        },
    });

export const trackTripxOfferClick = (offerId: string, hotelName: string, intent: 'Open' | 'Purchase') =>
    trackEvent({
        type: 'Click',
        intent: intent,
        name: 'Click on Tripx result card',
        object: {
            id: `click_on_offer_${offerId}`,
            type: 'UIElement',
            elementType: 'Link',
        },
        target: {
            id: offerId,
            type: 'ClassifiedAd',
            contentId: offerId,
            name: hotelName,
            category: 'travel > prepackage',
        },
        vertical: {
            name: 'travel',
            subVertical: 'prepackage',
        },
    });

export const trackDestinationFilterClick = (destination: string, checked: boolean) => {
    trackEvent({
        type: 'Click',
        intent: 'Filter',
        name: checked ? 'Deselect destination' : 'Select destination',
        object: {
            id: 'filter_destination_pre_package',
            name: destination,
            type: 'UIElement',
            elementType: 'Checkbox',
        },
        vertical: {
            name: 'travel',
            subVertical: 'prepackage',
        },
    });
};

// Only the most specific locations should be kept
// The location list is sorted, so if current value starts with the previous value, the previous value should be removed
const keepMostSpecific = (locations: string[]): string[] =>
    locations.reduce((accumulator: string[], currentValue: string) => {
        if (accumulator.length === 0) {
            return [currentValue];
        }
        const lastValue = accumulator.pop();
        if (lastValue && !currentValue.startsWith(lastValue)) {
            accumulator.push(lastValue);
        }
        accumulator.push(currentValue);
        return accumulator;
    }, []);

const flattenCheckboxTree = (tree: LmsCheckboxTree): string[] =>
    Object.keys(tree).flatMap((key: string) => [key, ...flattenCheckboxTree(tree[key])]);

// Pulse tracker id: TRL_pv01 (see tracking section in README.md)
export const trackLmsResultPage = (filters: LmsFilters, numFound?: number) =>
    trackPageView({
        object: {
            type: 'Listing',
            url: 'https://www.finn.no/reise/restplasser/',
            category: 'travel > last minute',
            layout: 'List',
            sortingType: filters.sorter ?? 'pris_lms',
            numItems: numFound ?? 0,
            pageNumber: filters.pageNumber ?? 1,
            selectionFilters: [
                {
                    name: 'departureDateRange',
                    value: filters.avreise ? [filters.avreise.from, filters.avreise.to].join('-') : undefined,
                },
                {
                    name: 'durationRange',
                    value: filters.lengde ? [filters.lengde.from, filters.lengde.to].join('-') : undefined,
                },
                {
                    name: 'type',
                    value: filters.type?.join(','),
                },
                {
                    name: 'priceRange',
                    value: filters.pris ? [filters.pris.from, filters.pris.to].join('-') : undefined,
                },
                {
                    name: 'stars',
                    value: filters.stjerner?.join(','),
                },
            ]
                .concat(filters.fra?.map((airport) => ({ name: 'airport', value: airport })) ?? [])
                .concat(keepMostSpecific(flattenCheckboxTree(filters.til ?? {})).map((location) => ({ name: 'location', value: location })))
                .concat(filters.med?.map((supplier) => ({ name: 'supplier', value: supplier })) ?? [])
                .filter((filter): filter is { name: string; value: string } => !!(filter.name && filter.value)),
        },
        vertical: {
            name: 'travel',
            subVertical: 'last_minute',
        },
    });

// Pulse tracker id: TRL_ev01 (see tracking section in README.md)
export const trackLmsClickOffer = (offer: LmsOffer) => {
    trackEvent({
        type: 'Click',
        intent: 'Purchase',
        name: 'Purchase last minute',
        object: {
            id: offer.offerId,
            type: 'UIElement',
            elementType: 'Button',
            items: [
                {
                    id: 'finntravel',
                    type: 'Travel',
                    travelType: 'last_minute',
                    departureCountry: 'Norge',
                    departureCity: offer.originCity,
                    departureAirport: offer.originAirportCode,
                    destination: offer.country,
                    arrivalCity: offer.destination,
                    departureDate: parseISO(offer.outboundDepartureTime).toISOString(),
                    returnDate: addDays(offer.duration, parseISO(offer.outboundDepartureTime)).toISOString(),
                    duration: Number(offer.duration),
                    totalPrice: Number(offer.price),
                    provider: offer.brand,
                },
            ],
        },
        target: {
            id: offer.deepLink,
            type: 'ExternalContent',
            name: 'Go to webpage',
            referralCommission: true,
            provider: offer.brand,
        },
        vertical: {
            name: 'travel',
            subVertical: 'last_minute',
        },
    });
};
